/**
 * In-app changelog. Newest first. Each day's bullets are surfaced on the
 * 设置 → 版本号 → 更新说明 page. Editorial rules:
 *   - Group by date (one entry per day, even if multiple commits shipped).
 *   - Feature-level changes are listed permanently (no expiry).
 *   - Bug fix entries: only include if shipped within the last 10 days;
 *     drop them from this file once they're older.
 *   - Skip pure refactors / docs / chore / CI / cosmetic-only tweaks.
 */

export type ChangelogEntryType = 'feat' | 'fix'

export interface ChangelogItem {
  type: ChangelogEntryType
  text: string
}

export interface ChangelogEntry {
  /** 'YYYY-MM-DD' in Beijing time. */
  date: string
  items: ChangelogItem[]
}

export const CHANGELOG: ChangelogEntry[] = [
  {
    date: '2026-10-01',
    items: [
      { type: 'feat', text: '新增预置运动「高位下拉」（背，默认 30kg）' },
    ],
  },
  {
    date: '2026-09-25',
    items: [
      { type: 'feat', text: '新增预置运动「杠铃卧推」（胸，默认 40kg）；另有「哑铃划船」（背）' },
      { type: 'feat', text: '带重量的运动可以在训练中随时调整「本次重量」，每组按当时的重量记录；结束时重量相同的组合并为一条记录，不同重量分别记录（例如卧推 40kg×2 组 + 30kg×1 组 会保存为两条），便于后期分析' },
    ],
  },
  {
    date: '2026-05-30',
    items: [
      { type: 'feat', text: '「周运动详情」支持补记有氧训练：进入修改模式后，有氧记录行尾会出现一个时钟图标，点击可修改开始时间和时长（适合忘记打卡时回填）' },
    ],
  },
  {
    date: '2026-05-28',
    items: [
      { type: 'feat', text: '新增 MCP 接口：登录用户可在「设置 → MCP 接口」生成 API Token，让 AI Agent 只读访问最近 365 天或指定区间（≤90 天）的运动数据；每个账号同时只能有 1 个 Token，每日最多调用 50 次' },
      { type: 'feat', text: '哑铃弯举等力量训练若每组重量不同，「周运动详情」可点击展开查看每组重量与次数（仅对新记录生效）' },
    ],
  },
  {
    date: '2026-05-23',
    items: [
      { type: 'feat', text: '新增「运动计划」：按星期设置训练内容；命中当天时，首页会显示「今日计划」提示' },
      { type: 'feat', text: '首页「本周」统计简化为摘要；详细分项移到「周运动详情」页面顶部' },
      { type: 'feat', text: '有氧训练退到后台 / 应用被关闭后的恢复窗口从 3 小时延长到 12 小时' },
      { type: 'fix', text: '修复部分用户从服务器拉取失败的问题（重复运动项导致）' },
    ],
  },
  {
    date: '2026-05-22',
    items: [
      { type: 'feat', text: '首页「我的运动」按部位分组（有氧 / 胸 / 背 / 肩 / 手臂 / 腿 / 核心 / 全身 / 自定义），紧凑卡片更密集' },
      { type: 'feat', text: '首页加入「管理」按钮，可在首页直接移除运动项目' },
      { type: 'fix', text: '修复数字输入框无法清空最后一位的问题' },
    ],
  },
  {
    date: '2026-05-21',
    items: [
      { type: 'feat', text: '所有 33 个预置运动加入「动作要点」（训练页右上角 💡 按钮查看）' },
      { type: 'feat', text: '平板支撑等倒计时类运动的统计同时显示组数和总时长' },
    ],
  },
  {
    date: '2026-05-20',
    items: [
      { type: 'feat', text: '新增月度活动日历，可按月浏览训练日 + 点击日期查看当日详情（首页右上角进入）' },
      { type: 'feat', text: '有氧训练（跑步、骑车等）支持退到后台 / 刷新 / 关闭后恢复继续' },
    ],
  },
  {
    date: '2026-05-19',
    items: [
      { type: 'feat', text: '新增 12 个预置运动：动感单车、哑铃卧推、哑铃飞鸟、杠铃划船、引体向上（窄握）、哑铃侧平举、哑铃前平举、直立划船、杠铃弯举、哑铃深蹲、单腿臀桥、弓步蹲' },
    ],
  },
  {
    date: '2026-05-18',
    items: [
      { type: 'feat', text: '添加运动的选择器按部位分组，方便快速找到目标运动' },
      { type: 'feat', text: '倒计时和组间休息结束时增加声音提示' },
      { type: 'feat', text: '设置页底部显示版本号，方便确认刷新是否到了最新版本' },
    ],
  },
  {
    date: '2026-05-17',
    items: [
      { type: 'feat', text: '架构重构：所有运动统一使用 4 种通用训练模板（计时 / 计圈 / 倒计时 / 手动计数）' },
      { type: 'feat', text: '哑铃类运动训练时支持记录每组次数和重量' },
      { type: 'feat', text: '训练页加齿轮按钮，可快速调整组间休息时长等参数' },
    ],
  },
  {
    date: '2026-05-16',
    items: [
      { type: 'feat', text: '内置 13 个预置运动（跑步、跳绳、椭圆机、划船机、高抬腿、开合跳、波比跳、登山者、仰卧起坐、卷腹、引体向上、双杠臂屈伸、硬拉），可一键添加到首页' },
    ],
  },
  {
    date: '2026-05-15',
    items: [
      { type: 'feat', text: '改为本地优先：访客无需登录即可使用，登录后开启云端备份' },
      { type: 'feat', text: '支持创建自定义运动（4 种训练模板 + 12 个图标可选）' },
      { type: 'feat', text: '可作为 PWA 安装到手机主屏幕 / 桌面' },
    ],
  },
  {
    date: '2026-04-22',
    items: [
      { type: 'feat', text: '新增「骑车」作为内置有氧运动' },
    ],
  },
  {
    date: '2026-04-18',
    items: [
      { type: 'feat', text: '邀请码注册：管理员生成邀请码后新用户才能注册；增加暴力破解防护' },
    ],
  },
  {
    date: '2026-04-17',
    items: [
      { type: 'feat', text: '设置页支持 JSON 数据的导入与导出（用于备份和迁移）' },
      { type: 'feat', text: '每个运动可以独立配置训练参数（组间休息、重量、单组时长等）' },
    ],
  },
  {
    date: '2026-04-16',
    items: [
      { type: 'feat', text: '新增「周运动详情」页面：按天展示一周内所有训练记录' },
    ],
  },
]
